import React, { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  IconButton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import HotelIcon from '@mui/icons-material/Hotel';
import PeopleIcon from '@mui/icons-material/People';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getConfigurationActionHeaderRowSx,
  getConfigurationActionHeaderTitleSx,
  getConfigurationAddButtonSx,
  getConfigurationCardGridSpacing,
  getConfigurationDrawerFooterSx,
  getConfigurationEmbeddedRootSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import {
  getConfigurationRoomTypes,
  createRoomType,
  updateRoomType,
  toggleRoomTypeStatus,
  deleteRoomType,
} from '../services/api';

const BOOKING_MODE_OPTIONS = [
  { value: 'NIGHTLY', label: 'Nightly Booking' },
  { value: 'HOURLY', label: 'Hourly Booking' },
];

const createEmptyForm = () => ({
  code: '',
  name: '',
  description: '',
  capacity: '',
  base_adults: '',
  base_children: '',
  max_guests: '',
  booking_mode: 'NIGHTLY',
  sort_order: '',
});

const drawerOutlinedFieldSx = (isDark) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4ce1b6',
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4ce1b6',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#4ce1b6',
  },
});

/** Outlined numeric field: no native spinners; MUI stepper buttons; value is number or '' (empty). */
function RoomTypeNumberField({
  label,
  required,
  value,
  onChange,
  min,
  max,
  helperText,
  error,
  isDark,
}) {
  const display = value === '' || value == null ? '' : String(value);
  const applyStep = (delta) => {
    if (delta < 0 && (value === '' || value === undefined || value === null)) return;
    let n;
    if (value === '' || value === undefined || value === null) {
      n = delta > 0 ? min : min;
    } else {
      n = Number(value) + delta;
    }
    n = Math.min(max, Math.max(min, n));
    onChange(n);
  };

  return (
    <TextField
      label={label}
      required={required}
      value={display}
      onChange={(e) => {
        const v = e.target.value.trim();
        if (v === '') {
          onChange('');
          return;
        }
        if (/^\d+$/.test(v)) {
          onChange(parseInt(v, 10));
        }
      }}
      fullWidth
      variant="outlined"
      error={error}
      helperText={helperText}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ mr: -0.5, maxHeight: 'none', alignSelf: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 0,
              }}
            >
              <IconButton
                size="small"
                tabIndex={-1}
                aria-label={`Increase ${label}`}
                onClick={(e) => {
                  e.preventDefault();
                  applyStep(1);
                }}
                sx={{
                  p: 0,
                  minWidth: 24,
                  minHeight: 0,
                  height: 17,
                  borderRadius: '2px',
                }}
              >
                <KeyboardArrowUpIcon sx={{ fontSize: 16, display: 'block' }} />
              </IconButton>
              <IconButton
                size="small"
                tabIndex={-1}
                aria-label={`Decrease ${label}`}
                onClick={(e) => {
                  e.preventDefault();
                  applyStep(-1);
                }}
                sx={{
                  p: 0,
                  minWidth: 24,
                  minHeight: 0,
                  height: 17,
                  borderRadius: '2px',
                }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 16, display: 'block' }} />
              </IconButton>
            </Box>
          </InputAdornment>
        ),
      }}
      sx={drawerOutlinedFieldSx(isDark)}
    />
  );
}

const RoomTypePage = () => {
  const isMobileLayout = isConfigurationMobileLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogState, setConfirmDialogState] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(() => createEmptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [imageErrors, setImageErrors] = useState(new Set());

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const isDark = currentTheme === 'dark';

  const fetchRoomTypes = async () => {
    setLoading(true);
    try {
      const data = await getConfigurationRoomTypes({ includeInactive: true });
      setRoomTypes(data);
    } catch (error) {setSnackbar({
        open: true,
        message: 'Failed to load room types. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (roomType = null) => {
    if (roomType) {
      // Edit mode
      setEditingId(roomType.id);
      setFormData({
        code: roomType.code,
        name: roomType.name,
        description: roomType.description || '',
        capacity: roomType.capacity,
        base_adults: roomType.base_adults,
        base_children: roomType.base_children,
        max_guests: roomType.max_guests,
        booking_mode: roomType.booking_mode,
        sort_order: roomType.sort_order,
      });
    } else {
      // Create mode — numeric fields start empty (no defaults in UI)
      setEditingId(null);
      setFormData(createEmptyForm());
    }
    setErrors({});
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingId(null);
    setFormData(createEmptyForm());
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSaveRoomType = async () => {
    setErrors({});
    const nextErrors = {};
    if (!formData.code?.trim()) nextErrors.code = 'Code is required';
    if (!formData.name?.trim()) nextErrors.name = 'Name is required';
    const reqInt = (raw, key, label, minV) => {
      if (raw === '' || raw == null) {
        nextErrors[key] = `${label} is required`;
        return null;
      }
      const n = Number(raw);
      if (!Number.isInteger(n) || n < minV) {
        nextErrors[key] = `Enter a whole number of at least ${minV}`;
        return null;
      }
      return n;
    };
    const capacity = reqInt(formData.capacity, 'capacity', 'Physical capacity', 1);
    const baseAdults = reqInt(formData.base_adults, 'base_adults', 'Base adults', 1);
    const maxGuests = reqInt(formData.max_guests, 'max_guests', 'Max guests', 1);
    let baseChildren = 0;
    if (formData.base_children !== '' && formData.base_children != null) {
      const bc = Number(formData.base_children);
      if (!Number.isInteger(bc) || bc < 0) {
        nextErrors.base_children = 'Enter a whole number of at least 0';
      } else {
        baseChildren = bc;
      }
    }
    let sortOrder = 0;
    if (formData.sort_order !== '' && formData.sort_order != null) {
      const so = Number(formData.sort_order);
      if (!Number.isInteger(so) || so < 0 || so > 99999) {
        nextErrors.sort_order = 'Enter a whole number between 0 and 99999';
      } else {
        sortOrder = so;
      }
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const payload = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      description: formData.description || '',
      capacity,
      base_adults: baseAdults,
      base_children: baseChildren,
      max_guests: maxGuests,
      booking_mode: formData.booking_mode,
      sort_order: sortOrder,
    };

    setSaving(true);

    try {
      let response;
      if (editingId) {
        // Update existing room type
        response = await updateRoomType(editingId, payload);
        setSnackbar({
          open: true,
          message: response.message || 'Room type updated successfully!',
          severity: 'success',
        });
      } else {
        // Create new room type
        response = await createRoomType(payload);
        setSnackbar({
          open: true,
          message: response.message || 'Room type created successfully!',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchRoomTypes();
    } catch (error) {if (error.errors) {
        setErrors(error.errors);
      } else {
        setSnackbar({
          open: true,
          message: error.error || `Failed to ${editingId ? 'update' : 'create'} room type. Please try again.`,
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (roomType) => {
    if (roomType?.is_deleted) {
      setSnackbar({
        open: true,
        message: 'Room type is already deleted.',
        severity: 'info',
      });
      return;
    }
    const hasAttachedRooms = Number(roomType?.total_rooms || 0) > 0;
    if (hasAttachedRooms) {
      setSnackbar({
        open: true,
        message: 'Cannot delete this room type because one or more rooms are using it. Disable it instead.',
        severity: 'warning',
      });
      return;
    }
    setConfirmDialogState({ action: 'delete', roomType });
    setConfirmInput('');
    setConfirmDialogOpen(true);
  };

  const handleToggleStatus = async (roomType) => {
    if (!roomType?.id) return;
    if (roomType.is_deleted) {
      setSnackbar({
        open: true,
        message: 'Deleted room type cannot be enabled. Create a new room type instead.',
        severity: 'warning',
      });
      return;
    }
    setConfirmDialogState({ action: roomType.is_active ? 'disable' : 'enable', roomType });
    setConfirmInput('');
    setConfirmDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    if (deletingId !== null || togglingId !== null) return;
    setConfirmDialogOpen(false);
    setConfirmDialogState(null);
    setConfirmInput('');
  };

  const handleConfirmAction = async () => {
    const targetRoomType = confirmDialogState?.roomType;
    if (!targetRoomType) return;
    const isDisableOrDeleteAction = ['disable', 'delete'].includes(confirmDialogState?.action);
    if (isDisableOrDeleteAction && targetRoomType?.has_active_bookings) {
      setSnackbar({
        open: true,
        message:
          targetRoomType?.active_booking_alert ||
          'Cannot proceed because active bookings exist for this room type.',
        severity: 'warning',
      });
      return;
    }

    if (confirmDialogState.action === 'delete') {
      setDeletingId(targetRoomType.id);
      try {
        await deleteRoomType(targetRoomType.id);
        setSnackbar({
          open: true,
          message: 'Room type deleted successfully!',
          severity: 'success',
        });
        setConfirmDialogOpen(false);
        setConfirmDialogState(null);
        setConfirmInput('');
        fetchRoomTypes();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.error || 'Failed to delete room type. Please try again.',
          severity: 'error',
        });
      } finally {
        setDeletingId(null);
      }
      return;
    }

    if (confirmDialogState.action === 'disable' || confirmDialogState.action === 'enable') {
      setTogglingId(targetRoomType.id);
      try {
        const isEnableAction = confirmDialogState.action === 'enable';
        const response = await toggleRoomTypeStatus(targetRoomType.id, isEnableAction);
        setSnackbar({
          open: true,
          message: response?.message || `Room type ${isEnableAction ? 'enabled' : 'disabled'} successfully!`,
          severity: 'success',
        });
        setConfirmDialogOpen(false);
        setConfirmDialogState(null);
        setConfirmInput('');
        fetchRoomTypes();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.error || 'Failed to update room type status. Please try again.',
          severity: 'error',
        });
      } finally {
        setTogglingId(null);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getRoomCountText = (count) => {
    return count === 1 ? '1 Room' : `${count} Rooms`;
  };

  const isDisableOrDeleteAction = ['disable', 'delete'].includes(confirmDialogState?.action);
  const isActionBlockedByBookings =
    isDisableOrDeleteAction && Boolean(confirmDialogState?.roomType?.has_active_bookings);
  const bookingBlockMessage =
    confirmDialogState?.roomType?.active_booking_alert ||
    'Active bookings exist in Confirmed/Check-In/Partial Check-In/Partial Check-Out status.';

  return (
    <Box sx={getConfigurationEmbeddedRootSx(isMobileLayout)}>
      {/* Header */}
      <Box sx={getConfigurationActionHeaderRowSx(isMobileLayout)}>
        <Typography
          variant="h5"
          sx={{
            ...getConfigurationActionHeaderTitleSx(isMobileLayout),
            fontWeight: 700,
            color: isDark ? '#fff' : '#212121',
          }}
        >
          Room Types
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDrawer}
          sx={getConfigurationAddButtonSx('#4ce1b6', isMobileLayout)}
        >
          Add Room Type
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Room Types Grid */}
      {!loading && (
        <Grid container spacing={getConfigurationCardGridSpacing(isMobileLayout)}>
          {roomTypes.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                No room types found. Click "Add Room Type" to create your first room type.
              </Alert>
            </Grid>
          ) : (
            roomTypes.map((roomType) => {
              const isActive = Boolean(roomType.is_active);
              const isDeleted = Boolean(roomType.is_deleted);
              const hasAttachedRooms = Number(roomType.total_rooms || 0) > 0;
              const hasActiveBookings = Boolean(roomType.has_active_bookings);
              const canDelete = !isDeleted && !hasAttachedRooms;
              return (
              <Grid item xs={12} sm={6} md={3} key={roomType.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    opacity: isDeleted ? 0.72 : 1,
                    filter: isDeleted ? 'grayscale(0.2)' : 'none',
                    '&:hover': {
                      boxShadow: isActive
                        ? (isDark
                            ? '0 8px 24px rgba(76, 225, 182, 0.2)'
                            : '0 8px 24px rgba(25, 118, 210, 0.2)')
                        : 'none',
                      transform: isActive ? 'translateY(-4px)' : 'none',
                    },
                  }}
                >
                  {/* Image or Placeholder */}
                  {roomType.image_url && !imageErrors.has(roomType.id) ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={roomType.image_url}
                      alt={roomType.name}
                      sx={{
                        objectFit: 'cover',
                        backgroundColor: isDark ? '#1a1a1f' : '#f5f5f5',
                      }}
                      onError={() => {
                        setImageErrors(prev => new Set([...prev, roomType.id]));
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isDark 
                          ? 'linear-gradient(165deg, THEME_COLORS.SURFACE_ELEVATED_DARK 0%, THEME_COLORS.BACKGROUND_DARK 100%)' 
                          : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                        borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                          color: isDark ? '#555' : '#999',
                        }}
                      >
                        <HotelIcon 
                          sx={{ 
                            fontSize: 64, 
                            opacity: 0.5,
                          }} 
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? '#666' : '#999',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            fontSize: '0.7rem',
                          }}
                        >
                          {roomType.name}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Content */}
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? '#fff' : '#212121',
                            mb: 0.5,
                            flexGrow: 1,
                          }}
                        >
                          {roomType.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={isActive ? 'Enabled' : 'Disabled'}
                          sx={{
                            height: 22,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            backgroundColor: isActive
                              ? (isDark ? 'rgba(76, 225, 182, 0.18)' : 'rgba(76, 225, 182, 0.14)')
                              : (isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.14)'),
                            color: isActive ? '#4ce1b6' : (isDark ? '#ffb74d' : '#ef6c00'),
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {isDeleted && (
                          <Chip
                            size="small"
                            label="Deleted"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: '0.68rem',
                              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.22)' : 'rgba(244, 67, 54, 0.16)',
                              color: isDark ? '#ff8a80' : '#c62828',
                            }}
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDrawer(roomType)}
                          disabled={isDeleted || deletingId === roomType.id || togglingId === roomType.id}
                          sx={{
                            color: isDeleted ? (isDark ? '#777' : '#bbb') : '#4ce1b6',
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip
                          title={
                            isDeleted
                              ? 'Already deleted'
                              : hasAttachedRooms
                                ? 'Cannot delete: room is using this room type'
                                : hasActiveBookings
                                  ? 'Active bookings found. Open to view details'
                                  : 'Delete room type'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(roomType)}
                              disabled={deletingId === roomType.id || togglingId === roomType.id || !canDelete}
                              sx={{
                                color: isDark ? '#ff5252' : '#d32f2f',
                                '&:hover': {
                                  backgroundColor: isDark ? 'rgba(255, 82, 82, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                },
                              }}
                            >
                              {deletingId === roomType.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Room Count */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <HotelIcon sx={{ fontSize: 16, color: isDark ? '#999' : '#757575' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? '#999' : '#757575',
                          fontWeight: 600,
                        }}
                      >
                        {getRoomCountText(roomType.total_rooms)}
                      </Typography>
                    </Box>

                    {/* Capacity Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <PeopleIcon sx={{ fontSize: 16, color: isDark ? '#999' : '#757575' }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? '#999' : '#757575',
                        }}
                      >
                        Capacity: {roomType.capacity} | Max Guests: {roomType.max_guests}
                      </Typography>
                    </Box>

                    {/* Booking Mode + Toggle (same row) */}
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#4ce1b6',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                        }}
                      >
                        {roomType.booking_mode_display || '-'}
                      </Typography>
                      <Tooltip
                        title={
                          isDeleted
                            ? 'Deleted room type cannot be enabled'
                            : isActive
                            ? 'Disable room type'
                            : 'Enable room type'
                        }
                      >
                        <span>
                          <IconButton
                            onClick={() => handleToggleStatus(roomType)}
                            disabled={isDeleted || togglingId === roomType.id || deletingId === roomType.id}
                            sx={{
                              p: 0,
                              color: isDeleted
                                ? (isDark ? '#666' : '#bbb')
                                : isActive
                                  ? (isDark ? '#ffb74d' : '#ef6c00')
                                  : '#4ce1b6',
                              '&:hover': {
                                backgroundColor: isDeleted
                                  ? 'transparent'
                                  : isActive
                                    ? (isDark ? 'rgba(255, 183, 77, 0.12)' : 'rgba(239, 108, 0, 0.1)')
                                    : (isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)'),
                              },
                            }}
                          >
                            {togglingId === roomType.id ? (
                              <CircularProgress size={30} />
                            ) : isActive ? (
                              <ToggleOnIcon sx={{ fontSize: 40 }} />
                            ) : (
                              <ToggleOffIcon sx={{ fontSize: 40 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              );
            })
          )}
        </Grid>
      )}

      {/* Add Room Type Drawer — layout matches CheckInDrawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HotelIcon sx={{ color: '#4ce1b6', fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                {editingId ? 'Edit Room Type' : 'Add New Room Type'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={{
                borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                color: '#4ce1b6',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1.5,
                py: 0.3,
                minWidth: 84,
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                },
                '&.Mui-disabled': {
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#d0d0d0',
                  color: isDark ? '#666' : '#999',
                },
              }}
            >
              Close
            </Button>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
              <TextField
                label="Code"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                fullWidth
                variant="outlined"
                required
                error={!!errors.code}
                helperText={errors.code}
                placeholder="e.g., STD, DLX, SUITE"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />

              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                fullWidth
                variant="outlined"
                required
                error={!!errors.name}
                helperText={errors.name}
                placeholder="e.g., Standard Room, Deluxe Suite"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                placeholder="Describe the room type..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" sx={{ color: isDark ? '#ddd' : '#666', fontWeight: 600 }}>
                Capacity Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <RoomTypeNumberField
                    label="Physical Capacity"
                    required
                    value={formData.capacity}
                    onChange={(v) => handleInputChange('capacity', v)}
                    min={1}
                    max={20}
                    error={!!errors.capacity}
                    helperText={errors.capacity || 'Maximum number of people the room can physically accommodate'}
                    isDark={isDark}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RoomTypeNumberField
                    label="Base Adults"
                    required
                    value={formData.base_adults}
                    onChange={(v) => handleInputChange('base_adults', v)}
                    min={1}
                    max={10}
                    error={!!errors.base_adults}
                    helperText={errors.base_adults || 'Number of adults included in base rate'}
                    isDark={isDark}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RoomTypeNumberField
                    label="Base Children"
                    required={false}
                    value={formData.base_children}
                    onChange={(v) => handleInputChange('base_children', v)}
                    min={0}
                    max={10}
                    error={!!errors.base_children}
                    helperText={errors.base_children || 'Number of children included in base rate (optional, default 0)'}
                    isDark={isDark}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RoomTypeNumberField
                    label="Max Guests"
                    required
                    value={formData.max_guests}
                    onChange={(v) => handleInputChange('max_guests', v)}
                    min={1}
                    max={20}
                    error={!!errors.max_guests}
                    helperText={errors.max_guests || 'Maximum total guests allowed for booking'}
                    isDark={isDark}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              <TextField
                label="Booking Mode"
                value={formData.booking_mode}
                onChange={(e) => handleInputChange('booking_mode', e.target.value)}
                fullWidth
                select
                variant="outlined"
                required
                error={!!errors.booking_mode}
                helperText={errors.booking_mode}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              >
                {BOOKING_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <RoomTypeNumberField
                label="Sort Order"
                required={false}
                value={formData.sort_order}
                onChange={(v) => handleInputChange('sort_order', v)}
                min={0}
                max={99999}
                error={!!errors.sort_order}
                helperText={errors.sort_order || 'Display order for sorting (optional, default 0)'}
                isDark={isDark}
              />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={getConfigurationDrawerFooterSx(isMobileLayout)}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCloseDrawer}
                disabled={saving}
                sx={{
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                  color: isDark ? '#ddd' : '#666',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#4ce1b6',
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                    color: '#4ce1b6',
                  },
                  '&:disabled': {
                    borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#666' : '#999',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSaveRoomType}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  backgroundColor: '#4ce1b6',
                  color: '#000',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#4ce1b6',
                    opacity: 0.9,
                    boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#999' : '#666',
                  },
                }}
              >
                {editingId ? 'Update Room Type' : 'Create Room Type'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmCancel}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? '#fff' : '#212121',
            fontWeight: 600,
            pb: 2,
          }}
        >
          {confirmDialogState?.action === 'delete'
            ? 'Delete Room Type'
            : confirmDialogState?.action === 'disable'
              ? 'Disable Room Type'
              : 'Enable Room Type'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: isDark ? '#ddd' : '#666',
              fontSize: '1rem',
            }}
          >
            {confirmDialogState?.action === 'delete' ? (
              <>
                To delete room type <strong>"{confirmDialogState?.roomType?.name}"</strong> ({confirmDialogState?.roomType?.code}), type <strong>confirm</strong> below.
              </>
            ) : confirmDialogState?.action === 'disable' ? (
              <>
                To disable room type <strong>"{confirmDialogState?.roomType?.name}"</strong> ({confirmDialogState?.roomType?.code}), type <strong>confirm</strong> below.
              </>
            ) : (
              <>
                To enable room type <strong>"{confirmDialogState?.roomType?.name}"</strong> ({confirmDialogState?.roomType?.code}), type <strong>confirm</strong> below.
              </>
            )}
          </DialogContentText>
          {isActionBlockedByBookings && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {bookingBlockMessage}
            </Alert>
          )}
          {confirmDialogState?.action === 'enable' && (
            <DialogContentText
              sx={{
                mt: 1,
                color: isDark ? '#ffcc80' : '#ef6c00',
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            >
              Default channel allocation will be set to full room count for this room type. You can adjust channel allocation manually from Distribution.
            </DialogContentText>
          )}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label='Type "confirm"'
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
          }}
        >
          <Button
            onClick={handleConfirmCancel}
            variant="outlined"
            sx={{
              color: '#4ce1b6',
              borderColor: '#4ce1b6',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              px: 2,
              '&:hover': {
                borderColor: '#4ce1b6',
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={
              confirmDialogState?.action === 'delete'
                ? 'error'
                : confirmDialogState?.action === 'disable'
                  ? 'warning'
                  : 'success'
            }
            disabled={
              (deletingId !== null || togglingId !== null) ||
              confirmInput.trim().toLowerCase() !== 'confirm' ||
              isActionBlockedByBookings
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              '&:disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
              },
            }}
          >
            {deletingId !== null || togglingId !== null ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Processing...
              </>
            ) : confirmDialogState?.action === 'delete' ? (
              'Delete'
            ) : confirmDialogState?.action === 'disable' ? (
              'Disable'
            ) : (
              'Enable'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            color: isDark ? '#fff' : '#212121',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoomTypePage;

